<template>
  <div>
    <el-table
      :data="pendingJobs"
      v-loading="loading"
      border
      class="app-table"
      empty-text="No jobs pending PQ sign-off"
    >
      <el-table-column prop="name" label="Job Name" min-width="180">
        <template slot-scope="{ row }">
          <el-button type="text" @click="toggleJob(row.id)">{{
            row.name
          }}</el-button>
        </template>
      </el-table-column>
      <el-table-column label="Site" min-width="160">
        <template slot-scope="{ row }">{{
          siteNameMap[row.siteId] || row.siteId || '-'
        }}</template>
      </el-table-column>
      <el-table-column label="Submitted" width="160">
        <template slot-scope="{ row }">{{
          formatDate(row.submittedAt)
        }}</template>
      </el-table-column>
      <el-table-column label="PQ Window Closed" width="180">
        <template slot-scope="{ row }">{{
          formatDate(row.pqUnlocksAt)
        }}</template>
      </el-table-column>
      <el-table-column label="Status" width="160" align="center">
        <template>
          <el-tag size="small" type="warning">Awaiting PQ Sign-off</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="" width="80" align="center">
        <template slot-scope="{ row }">
          <el-button type="text" size="small" @click="toggleJob(row.id)">
            <i
              :class="
                expandedJobId === row.id
                  ? 'el-icon-arrow-up'
                  : 'el-icon-arrow-down'
              "
            />
          </el-button>
        </template>
      </el-table-column>
    </el-table>

    <div v-if="expandedJob" :key="expandedJob.id" class="pq-card">
      <div class="pq-card__header" @click="toggleJob(expandedJob.id)">
        <div class="pq-card__title">
          <span class="job-name">{{ expandedJob.name }}</span>
          <el-tag size="small" type="warning">Awaiting PQ Sign-off</el-tag>
        </div>
        <div class="pq-card__meta">
          <i class="el-icon-arrow-up" />
        </div>
      </div>

      <div class="pq-card__body">
        <!-- PQ Metrics -->
        <div v-loading="loadingMetrics[expandedJob.id]">
          <h4 class="section-label">PQ Auto Checks</h4>
          <el-table
            :data="
              (metrics[expandedJob.id] && metrics[expandedJob.id].autoChecks) ||
                []
            "
            border
            size="small"
            class="mb-16"
            empty-text="No auto check data"
          >
            <el-table-column prop="checkName" label="Check" />
            <el-table-column
              prop="description"
              label="Description"
              min-width="200"
              show-overflow-tooltip
            />
            <el-table-column
              prop="measuredValue"
              label="Measured"
              width="140"
            />
            <el-table-column prop="threshold" label="Threshold" width="120" />
            <el-table-column
              prop="passed"
              label="Result"
              width="90"
              align="center"
            >
              <template slot-scope="{ row }">
                <el-tag :type="row.passed ? 'success' : 'danger'" size="small">
                  {{ row.passed ? 'PASS' : 'FAIL' }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>

          <h4 class="section-label">Device Metrics</h4>
          <el-table
            :data="
              (metrics[expandedJob.id] &&
                metrics[expandedJob.id].deviceMetrics) ||
                []
            "
            border
            size="small"
            class="mb-16"
            empty-text="No device metric data"
          >
            <el-table-column prop="deviceType" label="Type" width="120" />
            <el-table-column prop="deviceEui" label="EUI / ID" width="200" />
            <el-table-column
              prop="uplinkCount"
              label="Uplinks"
              width="100"
              align="center"
            />
            <el-table-column
              prop="packetLossPct"
              label="Packet Loss %"
              width="130"
              align="center"
            >
              <template slot-scope="{ row }">
                <span
                  :class="
                    row.packetLossPct > 10 ? 'text-danger' : 'text-success'
                  "
                >
                  {{
                    row.packetLossPct != null ? row.packetLossPct + '%' : '-'
                  }}
                </span>
              </template>
            </el-table-column>
            <el-table-column
              prop="avgRssi"
              label="Avg RSSI"
              width="110"
              align="center"
            />
            <el-table-column prop="lastSeen" label="Last Seen" width="160">
              <template slot-scope="{ row }">{{
                formatDate(row.lastSeen)
              }}</template>
            </el-table-column>
          </el-table>

          <!-- Sign-off form -->
          <div class="signoff-form">
            <h4 class="section-label">PQ Sign-off</h4>

            <el-alert
              v-if="hasFailingAutoChecks(expandedJob.id)"
              type="warning"
              :closable="false"
              title="One or more auto checks are failing. Override is required to sign off."
              show-icon
              style="margin-bottom:16px"
            />

            <el-form
              :model="signoffForms[expandedJob.id]"
              ref="signoffForm"
              label-position="top"
            >
              <el-form-item required>
                <el-checkbox
                  v-model="
                    signoffForms[expandedJob.id]
                      .dataReachingDestinationConfirmed
                  "
                >
                  <strong
                    >I confirm that data is reaching the destination system
                    correctly</strong
                  >
                </el-checkbox>
              </el-form-item>

              <template v-if="hasFailingAutoChecks(expandedJob.id)">
                <el-form-item>
                  <el-checkbox v-model="signoffForms[expandedJob.id].override"
                    >Override failing auto checks</el-checkbox
                  >
                </el-form-item>
                <el-form-item
                  v-if="signoffForms[expandedJob.id].override"
                  label="Override Notes"
                  required
                >
                  <el-input
                    type="textarea"
                    :rows="3"
                    v-model="signoffForms[expandedJob.id].notes"
                    placeholder="Explain why the failing checks are acceptable"
                  />
                </el-form-item>
              </template>

              <el-button
                v-if="$permission('Installation Tool.PQ-SignOff.Add')"
                type="success"
                :loading="signingOff[expandedJob.id]"
                :disabled="!canSignOff(expandedJob.id)"
                @click="signOff(expandedJob)"
              >
                Sign Off PQ — Mark Job Complete
              </el-button>
            </el-form>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import LoraApiService from '@/services/LoraApiService'
import { loraError } from '@/utils/loraError'
import moment from 'moment'
import Vue from 'vue'

export default {
  name: 'LoraPqPending',
  computed: {
    siteNameMap() {
      const map = {}
      this.sites.forEach(function(s) {
        map[s.id] = s.name
      })
      return map
    },
    currentUserId() {
      const u = this.$store.state.user.loggedInUser
      return u && u.id
    },
    expandedJob() {
      var self = this
      if (!self.expandedJobId) return null
      return (
        self.pendingJobs.find(function(j) {
          return j.id === self.expandedJobId
        }) || null
      )
    }
  },
  data() {
    return {
      pendingJobs: [],
      sites: [],
      loading: false,
      expandedJobId: null,
      metrics: {},
      loadingMetrics: {},
      signoffForms: {},
      signingOff: {}
    }
  },
  mounted() {
    this.loadSites()
    this.loadPendingJobs()
  },
  methods: {
    async loadSites() {
      try {
        const res = await LoraApiService.getSites()
        this.sites = res.data || []
      } catch {
        // non-critical
      }
    },

    async loadPendingJobs() {
      this.loading = true
      try {
        const res = await LoraApiService.getPendingPqJobs()
        this.pendingJobs = res.data || []
        this.pendingJobs.forEach(job => {
          if (!this.signoffForms[job.id]) {
            Vue.set(this.signoffForms, job.id, {
              dataReachingDestinationConfirmed: false,
              override: false,
              notes: ''
            })
          }
        })
      } catch {
        this.$message.error('Failed to load pending PQ jobs')
      } finally {
        this.loading = false
      }
    },

    async toggleJob(jobId) {
      if (this.expandedJobId === jobId) {
        this.expandedJobId = null
        return
      }
      this.expandedJobId = jobId
      if (!this.metrics[jobId]) {
        await this.loadMetrics(jobId)
      }
    },

    async loadMetrics(jobId) {
      Vue.set(this.loadingMetrics, jobId, true)
      try {
        const res = await LoraApiService.getPqMetrics(jobId)
        Vue.set(this.metrics, jobId, res.data)
      } catch (e) {
        this.$message.error(loraError(e, 'Failed to load PQ metrics'))
      } finally {
        Vue.set(this.loadingMetrics, jobId, false)
      }
    },

    hasFailingAutoChecks(jobId) {
      const m = this.metrics[jobId]
      if (!m || !m.autoChecks) return false
      return m.autoChecks.some(c => !c.passed)
    },

    canSignOff(jobId) {
      const form = this.signoffForms[jobId]
      if (!form) return false
      if (!form.dataReachingDestinationConfirmed) return false
      if (this.hasFailingAutoChecks(jobId)) {
        if (!form.override) return false
        if (!form.notes) return false
      }
      return true
    },

    async signOff(job) {
      const form = this.signoffForms[job.id]
      try {
        await this.$confirm(
          'Sign off PQ for "' +
            job.name +
            '"? This will mark the job as Complete.',
          'Confirm PQ Sign-off',
          { type: 'success', confirmButtonText: 'Sign Off' }
        )
      } catch {
        return
      }

      Vue.set(this.signingOff, job.id, true)
      try {
        await LoraApiService.pqSignoff(job.id, {
          userId: this.currentUserId,
          dataReachingDestinationConfirmed:
            form.dataReachingDestinationConfirmed,
          override: form.override || false,
          notes: form.notes || undefined
        })
        this.$message.success(
          'Job "' + job.name + '" signed off and marked Complete'
        )
        this.loadPendingJobs()
        this.expandedJobId = null
      } catch (e) {
        this.$message.error(loraError(e, 'Sign-off failed'))
      } finally {
        Vue.set(this.signingOff, job.id, false)
      }
    },

    formatDate(d) {
      return d ? moment(d).format('DD MMM YYYY HH:mm') : '-'
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/css/varible.scss';

.filter-bar {
  margin-bottom: 20px;
}

.pq-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  margin-bottom: 16px;
  overflow: hidden;

  &__header {
    padding: 16px 20px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid #f0f0f0;
    transition: background 0.15s;
    &:hover {
      background: #fafafa;
    }
  }
  &__title {
    display: flex;
    align-items: center;
    gap: 12px;
    .job-name {
      font-weight: 600;
      font-size: 16px;
      color: #1a2332;
    }
  }
  &__meta {
    display: flex;
    align-items: center;
    gap: 16px;
    font-size: 13px;
    color: $font-color;
  }
  &__body {
    padding: 20px;
  }
}

.section-label {
  font-size: 14px;
  font-weight: 600;
  color: #1a2332;
  margin: 0 0 12px;
}
.mb-16 {
  margin-bottom: 16px;
}

.signoff-form {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-top: 16px;
}

.text-success {
  color: #67c23a;
}
.text-danger {
  color: #f56c6c;
}
</style>
